import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  split,
  ApolloLink,
} from "@apollo/client";
import { relayStylePagination } from "@apollo/client/utilities";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";

import type { AuthProps } from "lib/context/auth";

export default async function graphqlAuthSetup (auth: AuthProps | null) {

  const testReportHttpLink = createHttpLink({
    uri: `${process.env.REACT_APP_GRAPHQL_API_URL_TEST_REPORTS}/graphql`,
    credentials: "include"
  });

  const monitoringInfoHttpLink = createHttpLink({
    uri: `${process.env.REACT_APP_GRAPHQL_API_URL_MONITORING_INFO}/api`,
    credentials: "include"
  });

  // The split function takes five parameters:

  // * A function that's called for each operation to execute
  // * The Link to use for an operation if the function returns a "truthy" value
  // * The Link to use for an operation if the function returns a "falsy" value

  const splitLink =
    split(
      (operation) => {
        return (operation.getContext().clientName === "testReport");
      },
      testReportHttpLink,
      monitoringInfoHttpLink
    );

  const authLink = setContext((_, { headers }) => {
    const token = auth?.user?.token ?? localStorage.getItem("monitoring_user_token");
    return {
      headers: {
        ...headers,
        ...(token != null ? { Authorization: `Bearer ${token}` } : {}),
      }
    };
  });

  const cache = new InMemoryCache({
    typePolicies: {
      ChargerMonitors: {
        fields: {
          charger_status_messages: relayStylePagination(),
        }
      },
      Query: {
        fields: {
          chargers: relayStylePagination(["@connection", ["key"]]),
          charger_monitors: {
            keyArgs: ["@connection", ["key"]]
          },
          users: relayStylePagination(),
          sites: {
            keyArgs: ["@connection", ["key"]]
          }
        }
      }
    }
  });

  const client = new ApolloClient({
    link: ApolloLink.from([
      onError((error) => {
        if ((error as unknown as {networkError: {result: {message: string}}})?.networkError?.result?.message?.toLowerCase().includes("not authenticated")) {
          auth?.setExpiredTokenError(true);
        }
        if (error?.graphQLErrors?.[0]?.message.includes("401") === true) {
          auth?.setExpiredTokenError(true);
        }
        if (
          error?.graphQLErrors?.[0].message?.toLowerCase().includes("unknown user") === true
          || error?.graphQLErrors?.[0].message?.toLowerCase().includes("user has no role") === true
        ) {
          auth?.setUnknownUserError(true);
        }
      }),
      authLink.concat(splitLink)
    ]),
    defaultOptions: {
      query: {
        fetchPolicy: "cache-first"
      },
      watchQuery: {
        fetchPolicy: "cache-first"
      },
    },
    connectToDevTools: true,
    cache
  });

  return { client };
}
